import { NetworkError } from '../errors'
import { HTTPStatusText, HTTPStatusCode } from '~/api/httpStatus'

export function newRequestId() {
  const requestId = crypto.randomUUID().replaceAll('-', '')
  return requestId
}

export function newIdempotencyKey() {
  return crypto.randomUUID()
}

/**
 * Read an error response body without ever throwing.
 *
 * A bare `await response.json()` rejects on an empty or non-JSON body — an HTML
 * page from a proxy, a 204, a truncated response — which would replace the real
 * failure with a SyntaxError at the exact moment we are trying to describe it.
 * The body is cloned so the text fallback can still read a stream that `json()`
 * has already consumed.
 */
export async function readErrorBody(response: Response): Promise<unknown> {
  try {
    return await response.clone().json()
  } catch {
    try {
      return await response.text()
    } catch {
      return undefined
    }
  }
}

/**
 * The 422 body phrase that means "retry this POST/PATCH with a fresh key".
 *
 * Exported so the verbs and their specs agree on the literal rather than each
 * carrying its own copy.
 */
export const IDEMPOTENCY_KEY_BODY_MISMATCH = 'Idempotency-Key body mismatch'

/** Longest body/message we will put in front of a user before truncating. */
const MAX_DESCRIPTION_LENGTH = 200

/** Field-level entries appended to the description before we stop. */
const MAX_EXTRA_ENTRIES = 5

/** First non-empty string among `keys`, or undefined. */
function firstNonEmptyString(
  body: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = body[key]
    // ⚠ Test the TYPE and the trimmed length, not truthiness: the msw auth
    // decorator sends `detail: ''`, and an object-valued `detail` would
    // stringify to the very '[object Object]' this function exists to prevent.
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }
  return undefined
}

/**
 * Litestar's `extra: [{ message, key }]` rendered as ` — key: message` pairs.
 *
 * This is the half that names the offending FIELD, which is the only part a
 * developer can act on — `split_fraction: Field required` versus a bare
 * "Validation failed". Capped so a 40-field rejection still groups usefully in
 * Honeybadger rather than producing a unique title per occurrence.
 */
function describeExtra(extra: unknown): string {
  if (!Array.isArray(extra)) {
    return ''
  }
  const parts: string[] = []
  for (const entry of extra.slice(0, MAX_EXTRA_ENTRIES)) {
    if (!entry || typeof entry !== 'object') {
      continue
    }
    const { key, message } = entry as Record<string, unknown>
    const keyText = typeof key === 'string' ? key : undefined
    const messageText = typeof message === 'string' ? message : undefined
    if (keyText && messageText) {
      parts.push(`${keyText}: ${messageText}`)
    } else if (messageText) {
      parts.push(messageText)
    }
  }
  if (!parts.length) {
    return ''
  }
  const more =
    extra.length > MAX_EXTRA_ENTRIES
      ? ` (+${extra.length - MAX_EXTRA_ENTRIES} more)`
      : ''
  return ` — ${parts.join('; ')}${more}`
}

function truncate(text: string): string {
  return text.length > MAX_DESCRIPTION_LENGTH
    ? `${text.slice(0, MAX_DESCRIPTION_LENGTH - 1)}…`
    : text
}

/**
 * Turn an error response body into a STRING a human can read.
 *
 * WHY THIS EXISTS: `getParsedErrorMessageFromRequestResponse` returned
 * `await response.json()` — the parsed OBJECT — while its name said "message"
 * and all four callers passed it straight into `new ServiceError(status, message)`.
 * `Error` coerces, so `.message` became the literal string '[object Object]'
 * before anything downstream could rescue it. It had no return-type annotation,
 * so TypeScript inferred `any` and never complained. Every 400 and 422 in the
 * app arrived that way, and Honeybadger grouped them all into one useless
 * bucket — which is why the 2026-08-15 production 400 (faults 133565364 /
 * 133565262, a user failing four times to add a team member to a service group)
 * could not be diagnosed from the fault alone.
 *
 * 🔑 Fix the PRODUCER, not the display. `handleAsyncDataErrors.ts` already
 * carries a comment about this symptom and `toDisplayMessage` was written to
 * defeat it, at the display layer — which cannot work, because the coercion has
 * already happened by then.
 *
 * Precedence: first NON-EMPTY string among `detail`, `message`, `error_code` /
 * `errorCode`, else `fallback`; then Litestar's field-level `extra[]` appended.
 */
export function describeErrorBody(body: unknown, fallback: string): string {
  if (typeof body === 'string') {
    const text = body.trim()
    // `readErrorBody` falls back to `.text()`, so a proxy's HTML error page can
    // land here. A 4 KB document must never become the toast.
    if (!text || text.startsWith('<') || text.length > MAX_DESCRIPTION_LENGTH) {
      return fallback
    }
    return text
  }

  if (!body || typeof body !== 'object') {
    return fallback
  }

  const record = body as Record<string, unknown>
  const head =
    firstNonEmptyString(record, [
      'detail',
      'message',
      'error_code',
      'errorCode',
    ]) ?? fallback

  return truncate(`${head}${describeExtra(record.extra)}`)
}

/**
 * Read an error response once, returning both a readable message and the raw
 * body — the message for `.message` (which is also the Honeybadger fault TITLE,
 * so it drives grouping) and the body for `context`.
 *
 * ⚠ Honeybadger drops custom Error properties: `__buildPayload` emits only a
 * fixed key set, so an own `body` property never reaches it. The reason has to
 * live in `.message`.
 */
export async function parseErrorResponse(
  response: Response
): Promise<{ message: string; body: unknown }> {
  const body = await readErrorBody(response)
  const fallback =
    (response.statusText && response.statusText.trim()) ||
    HTTPStatusText[response.status as HTTPStatusCode] ||
    'No error message'
  return { message: describeErrorBody(body, fallback), body }
}

/**
 * Does the raw body mention `phrase`?
 *
 * Probes the BODY rather than the formatted message, so the idempotency retry
 * never couples itself to display formatting. Accepts `unknown` and cannot
 * throw — the previous code called `.includes()` directly on the parsed value,
 * which threw `TypeError: parsed422.includes is not a function` on every object
 * body, destroying the real 422. That was dormant only because it sat behind
 * `asyncOptions?.doAsync`.
 */
export function bodyMentions(body: unknown, phrase: string): boolean {
  if (typeof body === 'string') {
    return body.includes(phrase)
  }
  if (!body || typeof body !== 'object') {
    return false
  }
  try {
    return JSON.stringify(body)?.includes(phrase) ?? false
  } catch {
    // A cyclic body cannot be serialised; it also cannot contain the phrase in
    // any way we could act on.
    return false
  }
}

/**
 * `fetch`, with a transport failure converted into a typed `NetworkError`.
 *
 * Every http verb wrote the same comment above its bare `await fetch(...)` —
 * "fetch throws network errors. HTTP status codes > 300 are not network
 * errors." — and then let that rejection escape the seam as a raw `TypeError`.
 * Use this instead of calling `fetch` directly, so the seam has exactly one
 * place where "the request never happened" is turned into something the rest
 * of the app can classify. See `NetworkError` for why that matters.
 *
 * A non-ok RESPONSE is deliberately still returned untouched: each verb has its
 * own status switch, and a 404/422/500 is a very different thing from never
 * having reached the server.
 */
export async function fetchOrThrowNetworkError(
  url: string,
  init: RequestInit
): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch (cause) {
    // AbortError is a deliberate client-side cancellation (navigating away,
    // a superseded search). It is not a server-reachability problem and must
    // not be reported or surfaced as one, so let it through as-is.
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw cause
    }
    throw new NetworkError(init.method ?? 'GET', url, cause)
  }
}
